'use client';
import React, { HTMLAttributes, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pill as PillIcon, Star as StarIcon } from 'lucide-react';
import Link from 'next/link';

import { PromoBannerType, type StoryGroup } from '@nutralab/api';
import { CarouselV2, Grid, Icon, Text } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import { StoriesRow } from './_components/StoriesRow';
import styles from './MainRecommendationBlock.module.scss';

import { BannerHtml } from '@/app/_components/BannerHtml';
import { useSiteConfig } from '@/app/_providers/SiteConfigProvider';
import { CatalogSearch } from '@/app/(home)/_components/CatalogSearch';

const CAROUSEL_AUTOPLAY_INTERVAL_MS = 5000;
const MAX_SUB_HEADER_CARDS = 4;

interface MainRecommendationBlockProps extends HTMLAttributes<HTMLDivElement> {
    stories: StoryGroup[];
}

/** The top of the home page: CMS banners, section links, the catalog search and the stories. */
export function MainRecommendationBlock({
    className,
    stories,
    ...props
}: MainRecommendationBlockProps): React.ReactElement {
    const { t } = useTranslation();
    const { promo_banners } = useSiteConfig();

    const carouselSlides = useMemo(
        () => promo_banners.filter((b) => b.type === PromoBannerType.CAROUSEL),
        [promo_banners],
    );

    const subHeaderCards = useMemo(
        () => promo_banners.filter((b) => b.type === PromoBannerType.SUB_HEADER).slice(0, MAX_SUB_HEADER_CARDS),
        [promo_banners],
    );

    const headerBanner = promo_banners.find((b) => b.type === PromoBannerType.HEADER);

    return (
        <div className={classNames(styles.wrapper, 'mx-3 md:mx-8', className)} {...props}>
            <Grid columns={{ base: 1, xl: 2 }} gap={8} rowGap={4}>
                <div className={styles['section-links']}>
                    <Link className={classNames(styles['section-link'], styles['is-centered'])} href="/#products">
                        <Icon icon={PillIcon} size="2xl" color="primary" className="mr-2" />
                        <Text size="lg" weight="normal" color="primary">
                            {t('navbar.products')}
                        </Text>
                    </Link>

                    <Link className={styles['section-link']} href="/#reviews">
                        <Icon icon={StarIcon} size="2xl" className="mr-2" />
                        <Text size="lg" weight="normal">
                            {t('navbar.reviews')}
                        </Text>
                    </Link>
                </div>

                <CatalogSearch />
            </Grid>

            <div className={styles['main-banners']}>
                <div className={classNames(styles['banner-cell'], styles['carousel-cell'], 'mx-auto md:mx-0')}>
                    <CarouselV2
                        autoplayInterval={CAROUSEL_AUTOPLAY_INTERVAL_MS}
                        labels={{ previous: t('carousel.previous'), next: t('carousel.next') }}
                    >
                        {carouselSlides.map((banner, i) => (
                            <BannerHtml
                                key={i}
                                html={banner.html}
                                imageLoading={i === 0 ? 'priority' : 'background'}
                                className={styles.banner}
                            />
                        ))}
                    </CarouselV2>
                </div>

                {headerBanner && (
                    <div className={classNames(styles['banner-cell'], 'mx-auto md:mx-0')}>
                        <BannerHtml html={headerBanner.html} imageLoading="eager" className={styles.banner} />
                    </div>
                )}
            </div>

            {subHeaderCards.length > 0 && (
                <div
                    className={classNames(
                        styles['sub-header-cards'],
                        subHeaderCards.length === 1 && styles['count-1'],
                        subHeaderCards.length === 2 && styles['count-2'],
                    )}
                >
                    {subHeaderCards.map((card, i) => (
                        <BannerHtml key={i} html={card.html} className={styles.banner} />
                    ))}
                </div>
            )}

            <StoriesRow groups={stories} className="mt-4 md:mt-16" />
        </div>
    );
}
